import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { crypto as cryptoShared } from '@photo-vault/shared';
import type { dto } from '@photo-vault/shared';
import { fromBase64Url } from '@salvobee/crypto-vault';
import {
  AntiAutofillHoneypot,
  Button,
  Card,
  Icon,
  Input,
  PageHeader,
  ProviderIcon,
} from '../../ui/index.js';
import { useAuth } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { config } from '../../lib/config.js';

type Step = 'idle' | 'cognito' | 'derive' | 'verify' | 'done';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession } = useAuth();
  const { auth, metadata } = useInfra();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [step, setStep] = useState<Step>('idle');
  const [error, setError] = useState<string | null>(null);

  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/';

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      setStep('cognito');
      const cognito = await auth.signIn(email, password);

      const bootstrap = await metadata.loginBootstrap(cognito.idToken);

      setStep('derive');
      const salt = fromBase64Url(bootstrap.saltB64);
      const { masterKey, verifierB64 } = await cryptoShared.deriveMaster(passphrase, salt);

      setStep('verify');
      const verify = await metadata.verifyPassphrase(
        { verifierB64 } satisfies dto.VerifyPassphraseRequest,
        cognito.idToken,
      );
      if (!verify.ok) {
        throw new Error('Passphrase non corretta.');
      }

      const rsaPriv = await cryptoShared.unwrapPrivateKey(bootstrap.wrappedPriv, masterKey);

      setSession({
        userId: bootstrap.userId,
        email: bootstrap.email,
        jwt: cognito.idToken,
        masterKey,
        rsaPriv,
        rsaPubB64: bootstrap.rsaPubB64,
        saltB64: bootstrap.saltB64,
        provider: 'native',
      });

      setStep('done');
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setStep('idle');
      setError(err instanceof Error ? err.message : 'Login fallito.');
    }
  }

  const busy = step !== 'idle' && step !== 'done';

  return (
    <div className="flex w-full max-w-md flex-col gap-7">
      <PageHeader
        eyebrow="encryption active"
        title={
          <>
            Sblocca il <span className="accent-text">tuo vault</span>
          </>
        }
        align="center"
      />

      <Card className="flex flex-col gap-5 sm:p-7">
        {config.cognitoHostedDomain ? (
          <div className="flex flex-col gap-3">
            <Button
              type="button"
              variant="outline"
              size="lg"
              fullWidth
              leftIcon={<ProviderIcon provider="google" />}
              onClick={() => auth.startFederatedLogin('Google')}
            >
              Continua con Google
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              fullWidth
              leftIcon={<ProviderIcon provider="apple" />}
              onClick={() => auth.startFederatedLogin('SignInWithApple')}
            >
              Continua con Apple
            </Button>
            <div className="flex items-center gap-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-on-surface-variant">
              <span className="h-px flex-1 bg-outline" />
              oppure email
              <span className="h-px flex-1 bg-outline" />
            </div>
          </div>
        ) : null}
        <form className="flex flex-col gap-3" onSubmit={handleSubmit} autoComplete="off">
          <AntiAutofillHoneypot />
          <Input
            type="email"
            label="Email"
            placeholder="me@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            leftIcon={<Icon name="alternate_email" className="text-base" />}
            autoComplete="email"
            required
          />
          <Input
            type="password"
            label="Password Cognito"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            leftIcon={<Icon name="key" className="text-base" />}
            autoComplete="current-password"
            required
          />
          <Input
            type="password"
            label="Passphrase E2EE"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            leftIcon={<Icon name="lock" className="text-base" />}
            autoComplete="off"
            hint="Sblocca le chiavi sul dispositivo."
            required
          />

          {error ? (
            <div className="rounded-lg border border-error/60 bg-error-container/30 px-4 py-3 text-sm text-error">
              {error}
            </div>
          ) : null}

          <Button
            type="submit"
            size="lg"
            fullWidth
            loading={busy}
            rightIcon={!busy ? <Icon name="arrow_forward" className="text-base" /> : null}
          >
            {busy ? labelForStep(step) : 'Sblocca il vault'}
          </Button>

          <p className="pt-2 text-center text-sm text-on-surface-variant">
            <button
              type="button"
              className="font-semibold text-cyan transition-colors hover:underline"
              onClick={() => navigate('/recovery')}
            >
              Passphrase E2EE dimenticata?
            </button>
          </p>

          <p className="text-center text-sm text-on-surface-variant">
            Non hai ancora un vault?{' '}
            <button
              type="button"
              className="font-semibold text-cyan transition-colors hover:underline"
              onClick={() => navigate('/signup')}
            >
              Crealo ora
            </button>
          </p>
        </form>
      </Card>
    </div>
  );
}

function labelForStep(step: Step): string {
  switch (step) {
    case 'cognito':
      return 'Autentico Cognito…';
    case 'derive':
      return 'Derivo la chiave master…';
    case 'verify':
      return 'Verifico la passphrase…';
    case 'done':
      return 'Fatto';
    default:
      return 'Attendi…';
  }
}
